import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { StateView, useAuth } from "@ozpanel/ui";

import { OperationRunner, type Operation } from "./OperationRunner";

/**
 * THE OPERATIONS FLOOR — every write one module declares, rendered from its own API documentation.
 *
 * Measured 2026-09-01: 201 of 450 write operations were reachable from no console; after removing
 * planned 501 stubs, 117 real ones. Not unfinished — handler, schema and daemon config all present
 * — simply uncalled. Hand-writing 117 buttons fixes today's list and begins accumulating
 * tomorrow's, so this is derived: a route becomes reachable the moment it is declared.
 *
 * The RUNNER is shared with the per-domain grouping (CategoryOperations) rather than copied. Every
 * defect worth reporting this week came from two copies of one thing drifting apart.
 *
 * This is a floor, not a replacement. A module with a purpose-built screen keeps it — that screen
 * knows the domain and this cannot. What this guarantees is that "there is no way to do X from the
 * panel" stops being true while X is a documented route.
 */
export function ModuleOperations({
  module,
  /** Where this module's real work is shown, if that is another screen. Passed in so the empty
   *  state can point somewhere instead of being a dead end. */
  screen,
  /** The module's own one-line description, so an empty page still says what the thing IS. */
  description,
}: {
  module: string;
  screen?: { label: string; to: string };
  description?: string;
}) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["module-operations", module],
    queryFn: () =>
      client.apiFetch<{ operations: Operation[]; undocumented: number; note?: string }>(
        `/ozpanel/modules/${encodeURIComponent(module)}/operations`,
      ),
  });

  const ops = q.data?.operations ?? [];

  return (
    <div className="space-y-3">
      <div className="text-[12.5px] leading-relaxed text-muted">
        Every write <span className="font-mono">{module}</span> declares, built from its own API
        documentation. Some are normally performed by a cluster step or the reconciler — they are
        here because the moment you need to run one by hand is when the automatic path is what has
        failed.
      </div>

      <StateView loading={q.isLoading} error={q.error} errorContext="this module's operations">
        {ops.length === 0 ? (
          // "No write operations" on its own is a dead end, and on a module that ALSO has no
          // settings, no config drift and no alerts it is the whole page. Five of the 49 modules
          // are in that state. So say what the module is, say plainly that there is nothing here
          // to operate, and send the reader where the work actually is.
          <div className="space-y-2 rounded-lg border border-line px-3 py-2.5 text-[12.5px] leading-relaxed">
            {description && <p>{description}</p>}
            <p className="text-muted">
              This module performs no write operations — there is nothing here to run, change or
              delete, and that is deliberate rather than unfinished.
            </p>
            {screen && (
              <p>
                What it holds is shown on{" "}
                <Link to={screen.to} className="underline underline-offset-2">
                  {screen.label}
                </Link>
                .
              </p>
            )}
          </div>
        ) : (
          <OperationRunner operations={ops} />
        )}
      </StateView>
    </div>
  );
}
